/*
 * Purpose: Human-readable map block summaries — SSOT for node overlays + mission goal
 * Last modified: 2026-07-10 | Agent
 * Completeness: 100
 */
import type { Node, Workflow } from '@/stores/workflowStore';
import { getNodeLabel } from '@/lib/workflowUtils';
import { getBlockTypeLabel } from '@/lib/blockTypes';

/** Plain-English purpose line shown on every map block overlay. */
export function getNodePurposeLine(node: Node): string {
  if (node.type === 'start') {
    const message = String(node.config.message ?? '').trim();
    if (message) return message;
    return 'Mission entry — every run starts here';
  }

  if (node.type === 'goal' && node.subtype === 'outcome') {
    const outcome = String(node.config.outcome ?? '').trim();
    if (outcome) return outcome;
    return 'Set the outcome this Mission should achieve';
  }

  const blockIntent = String(node.config.blockIntent ?? '').trim();
  if (blockIntent) {
    const taskOutcome = String(node.config.taskOutcome ?? '').trim();
    return taskOutcome ? `${blockIntent} → ${taskOutcome}` : blockIntent;
  }

  const rationale = String(node.config._autoBlocksRationale ?? '').trim();
  if (rationale) return rationale;

  const purpose = String(
    node.config.contextPurpose ?? node.config.agentBrief ?? node.config.droneTask ?? ''
  ).trim();
  if (purpose) return purpose;

  const message = String(node.config.message ?? '').trim();
  if (message) return message;

  const typeLabel = getBlockTypeLabel(node.type, node.subtype);
  const shortType = typeLabel.replace(/\s*Block$/i, '').trim();
  return `Configure this ${shortType.toLowerCase()} step — tap to edit or ask the Map AI Assistant.`;
}

export function getNodeSummaryOverlay(node: Node): {
  title: string;
  typeLabel: string;
  summary: string;
} {
  const title = getNodeLabel(node);
  const typeLabel = getBlockTypeLabel(node.type, node.subtype);
  const summary = getNodePurposeLine(node);
  return { title, typeLabel, summary };
}

/** Mission goal intent — GOAL outcome, then flow name, then fallback copy. */
export function getMissionGoalIntent(workflow: Workflow | null | undefined): string {
  if (!workflow) {
    return 'Describe your outcome on the GOAL block or ask the Map AI Assistant to plan a flow.';
  }

  const goal = workflow.nodes.find((n) => n.type === 'goal' && n.subtype === 'outcome');
  const outcome = String(goal?.config?.outcome ?? '').trim();
  if (outcome) return outcome;

  const name = workflow.name.trim();
  if (name && name !== 'Untitled flow') return name;

  return 'Add blocks and wire them from START to GOAL to automate this Mission.';
}
